import { useState, useEffect } from "react";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { UploadSimple, FileText, Trash, FilePdf, Wallet, Plus, Lightning } from "@phosphor-icons/react";
import { toast } from "sonner";
import api, { formatApiError, API } from "@/lib/api";
import { PaymentStatusBadge } from "@/components/StatusBadge";
import PaymentDeleteDialog from "@/components/PaymentDeleteDialog";
import { useInvoiceViewer } from "@/components/InvoiceViewer";

const CREATOR_STATUSES = ["Prospect", "Shortlisted", "Locked", "Live", "Cancelled"];
const SCRIPT_STATUSES = [
  "Script Pending", "Script Shared with Brand", "Rework on Script",
  "Script Approved", "Shoot Done", "Video Shared", "Video Rework",
  "Video Approved", "Live",
];

const EMPTY = {
  username: "", platform: "Instagram", link: "", followers: 0, city: "", state: "", language: "",
  gender: "", genre: "", deliverables: "",
  phone: "", email: "", status: "Prospect",
  first_cost: 0, final_cost: 0, internal_cost: 0, client_cost: 0,
  script_status: "Script Pending", script_text: "", live_date: "", live_link: "",
  invoice_type: "Non-GST", invoice_file_id: null, invoice_filename: null, invoice_number: "", invoice_date: "",
  legal_name: "", address: "", pan_number: "", bank_account_name: "",
  bank_account_number: "", bank_ifsc: "", bank_name: "", gst_number: "",
  views: 0, likes: 0, comments: 0, shares: 0, notes: "",
};

const PLATFORMS = ["Instagram", "YouTube", "LinkedIn", "X"];

const fmtCur = (n) => "₹" + Number(n || 0).toLocaleString("en-IN", { maximumFractionDigits: 0 });

export default function CreatorDialog({ open, onOpenChange, campaignId, campaignType, editing, onSaved, onOpenPayments, isAdmin, onOpenRazorpay }) {
  const [form, setForm] = useState(EMPTY);
  const [saving, setSaving] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [payments, setPayments] = useState([]);
  const [paymentToDelete, setPaymentToDelete] = useState(null);
  const { openInvoice } = useInvoiceViewer();

  useEffect(() => {
    if (editing) setForm({ ...EMPTY, ...editing });
    else setForm(EMPTY);
  }, [editing, open]);

  useEffect(() => {
    if (open && editing?.id) {
      api.get(`/creators/${editing.id}/payments`).then(({ data }) => setPayments(data)).catch(() => setPayments([]));
    } else {
      setPayments([]);
    }
    // eslint-disable-next-line
  }, [open, editing?.id]);

  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const save = async () => {
    if (!form.username.trim()) { toast.error("Username is required"); return; }
    setSaving(true);
    try {
      const payload = { ...form };
      ["followers", "views", "likes", "comments", "shares"].forEach((k) => (payload[k] = parseInt(payload[k] || 0) || 0));
      ["first_cost", "final_cost", "internal_cost", "client_cost"].forEach((k) => (payload[k] = parseFloat(payload[k] || 0) || 0));
      // gst_invoice_entries is historical; strip to avoid clobbering backend list
      delete payload.gst_invoice_entries;
      const res = editing?.id
        ? await api.put(`/creators/${editing.id}`, payload)
        : await api.post(`/campaigns/${campaignId}/creators`, payload);
      toast.success(editing ? "Creator updated" : "Creator added");
      onSaved?.(res.data);
      if (!editing) onOpenChange(false);
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || "Save failed");
    } finally { setSaving(false); }
  };

  const uploadInvoice = async (file) => {
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      // Detached upload — stages the file but does NOT persist on the creator
      // until the user clicks "Save changes" (then PUT /creators/{id} includes invoice_file_id).
      const { data } = await api.post(`/invoices/upload`, fd, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      set("invoice_file_id", data.file_id);
      set("invoice_filename", data.filename);
      toast.success("Invoice staged — click Save changes to persist");
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || "Upload failed");
    } finally { setUploading(false); }
  };

  const isLocked = form.status === "Locked" || form.status === "Live";
  const totalPaid = payments.reduce((s, p) => s + Number(p.amount || 0), 0);
  const pending = Math.max((Number(form.final_cost) || 0) - totalPaid, 0);

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-3xl max-h-[92vh] overflow-y-auto" data-testid="creator-dialog">
        <DialogHeader>
          <DialogTitle className="font-heading text-2xl flex items-center gap-3">
            {editing ? `@${editing.username}` : "Add creator"}
            {editing && <PaymentStatusBadge value={editing.payment_status} />}
          </DialogTitle>
        </DialogHeader>

        <Tabs defaultValue="info" className="mt-2">
          <TabsList className="grid grid-cols-4 mb-4">
            <TabsTrigger value="info" data-testid="tab-info">Info</TabsTrigger>
            <TabsTrigger value="script" disabled={!isLocked} data-testid="tab-script">Script</TabsTrigger>
            <TabsTrigger value="invoice" disabled={!isLocked} data-testid="tab-invoice">Invoice</TabsTrigger>
            <TabsTrigger value="payment" disabled={!isLocked} data-testid="tab-payment">Payments</TabsTrigger>
          </TabsList>

          {/* Info */}
          <TabsContent value="info" className="space-y-3">
            <div className="grid grid-cols-2 gap-3">
              <Field label="Username *" v={form.username} on={(v) => set("username", v)} testid="field-username" />
              <div>
                <Label className="text-xs uppercase tracking-wider text-slate-500">Platform</Label>
                <Select value={form.platform || "Instagram"} onValueChange={(v) => set("platform", v)}>
                  <SelectTrigger className="mt-1" data-testid="field-platform"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    {PLATFORMS.map((p) => <SelectItem key={p} value={p} data-testid={`platform-opt-${p.toLowerCase()}`}>{p}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label className="text-xs uppercase tracking-wider text-slate-500">Status</Label>
                <Select value={form.status} onValueChange={(v) => set("status", v)}>
                  <SelectTrigger className="mt-1" data-testid="field-creator-status"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    {CREATOR_STATUSES.map((s) => <SelectItem key={s} value={s}>{s}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
              <Field label="Profile link" v={form.link} on={(v) => set("link", v)} testid="field-link" />
              <Field label="Followers" type="number" v={form.followers} on={(v) => set("followers", v)} testid="field-followers" />
              <Field label="City" v={form.city} on={(v) => set("city", v)} testid="field-city" />
              <Field label="State" v={form.state} on={(v) => set("state", v)} testid="field-state" />
              <Field label="Language" v={form.language} on={(v) => set("language", v)} testid="field-language" />
              <div>
                <Label className="text-xs uppercase tracking-wider text-slate-500">Gender</Label>
                <Select value={form.gender || undefined} onValueChange={(v) => set("gender", v)}>
                  <SelectTrigger className="mt-1" data-testid="field-gender"><SelectValue placeholder="Select" /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="Male">Male</SelectItem>
                    <SelectItem value="Female">Female</SelectItem>
                    <SelectItem value="Other">Other</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <Field label="Genre" v={form.genre} on={(v) => set("genre", v)} testid="field-genre" />
              <Field label="Phone" v={form.phone} on={(v) => set("phone", v)} testid="field-phone" />
              <Field label="Email" v={form.email} on={(v) => set("email", v)} testid="field-email" />
            </div>
            <div>
              <Label className="text-xs uppercase tracking-wider text-slate-500">Deliverables</Label>
              <Textarea
                value={form.deliverables || ""}
                onChange={(e) => set("deliverables", e.target.value)}
                placeholder="e.g. 1 reel + 3 stories"
                rows={2}
                className="mt-1"
                data-testid="field-deliverables"
              />
            </div>
            <div className="grid grid-cols-2 gap-3 pt-2 mt-2 border-t border-slate-200">
              <Field label="Initial cost (₹)" type="number" v={form.first_cost} on={(v) => set("first_cost", v)} testid="field-first-cost" />
              <Field label="Final cost (₹)" type="number" v={form.final_cost} on={(v) => set("final_cost", v)} testid="field-final-cost" />
            </div>
            {Number(form.final_cost) > 0 && (
              <div className="pt-2 border-t border-slate-200 text-xs text-slate-600" data-testid="auto-tds-info">
                <div className="text-[10px] uppercase tracking-widest text-slate-500 mb-1">Auto tax (from invoice type)</div>
                {form.invoice_type === "GST" ? (
                  <>
                    GST invoice · <b>2% TDS</b> withheld (₹{Math.round(Number(form.final_cost) * 0.02).toLocaleString("en-IN")}) · <b>18% GST</b> reimbursement pending (₹{Math.round(Number(form.final_cost) * 0.18).toLocaleString("en-IN")})
                  </>
                ) : (
                  <>Non-GST invoice · <b>1% TDS</b> withheld (₹{Math.round(Number(form.final_cost) * 0.01).toLocaleString("en-IN")})</>
                )}
                <span className="text-slate-400"> · applied when the Final Settlement payment is logged</span>
              </div>
            )}
            {campaignType === "Creator Based" && (
              <div className="pt-2 border-t border-slate-200" data-testid="creator-based-costs">
                <div className="text-[10px] uppercase tracking-widest text-slate-500 mb-1.5">Creator based · margin</div>
                <div className="grid grid-cols-3 gap-3 items-end">
                  <Field label="Internal cost (₹)" type="number" v={form.internal_cost} on={(v) => set("internal_cost", v)} testid="field-internal-cost" />
                  <Field label="Client cost (₹)" type="number" v={form.client_cost} on={(v) => set("client_cost", v)} testid="field-client-cost" />
                  <div>
                    <Label className="text-xs uppercase tracking-wider text-slate-500">Margin</Label>
                    <div
                      className={`mt-1 h-9 px-3 flex items-center rounded-md border font-mono text-sm font-semibold ${
                        (Number(form.client_cost) || 0) - (Number(form.internal_cost) || 0) >= 0
                          ? "border-emerald-200 bg-emerald-50 text-emerald-700"
                          : "border-rose-200 bg-rose-50 text-rose-700"
                      }`}
                      data-testid="creator-margin"
                    >
                      {fmtCur((Number(form.client_cost) || 0) - (Number(form.internal_cost) || 0))}
                    </div>
                  </div>
                </div>
                <div className="text-[11px] text-slate-500 mt-1.5">Internal cost = what you pay the creator. Client cost = what you charge the client.</div>
              </div>
            )}
            <div>
              <Label className="text-xs uppercase tracking-wider text-slate-500">Notes</Label>
              <Textarea value={form.notes} onChange={(e) => set("notes", e.target.value)} rows={2} className="mt-1" data-testid="field-notes" />
            </div>
          </TabsContent>

          {/* Script */}
          <TabsContent value="script" className="space-y-3">
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label className="text-xs uppercase tracking-wider text-slate-500">Script status</Label>
                <Select value={form.script_status} onValueChange={(v) => set("script_status", v)}>
                  <SelectTrigger className="mt-1" data-testid="field-script-status"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    {SCRIPT_STATUSES.map((s) => <SelectItem key={s} value={s}>{s}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
              <Field label="Live date" type="date" v={form.live_date} on={(v) => set("live_date", v)} testid="field-live-date" />
              <Field label="Live link" v={form.live_link} on={(v) => set("live_link", v)} testid="field-live-link" wide />
            </div>
            <div>
              <Label className="text-xs uppercase tracking-wider text-slate-500 flex items-center justify-between">
                <span>Script</span>
                <span className="text-[10px] text-slate-400 normal-case tracking-normal">
                  {form.script_text ? `${form.script_text.length} chars` : "Paste the script or hook here"}
                </span>
              </Label>
              <Textarea
                value={form.script_text}
                onChange={(e) => set("script_text", e.target.value)}
                rows={6}
                placeholder="Paste the approved script, hook, or brief the creator will follow…"
                className="mt-1 font-mono text-xs"
                data-testid="field-script-text"
              />
            </div>
            <div className="grid grid-cols-4 gap-3 pt-2 border-t border-slate-200">
              <Field label="Views" type="number" v={form.views} on={(v) => set("views", v)} testid="field-views" />
              <Field label="Likes" type="number" v={form.likes} on={(v) => set("likes", v)} testid="field-likes" />
              <Field label="Comments" type="number" v={form.comments} on={(v) => set("comments", v)} testid="field-comments" />
              <Field label="Shares" type="number" v={form.shares} on={(v) => set("shares", v)} testid="field-shares" />
            </div>
          </TabsContent>

          {/* Invoice */}
          <TabsContent value="invoice" className="space-y-3">
            <div className="flex gap-3">
              <button
                onClick={() => set("invoice_type", "GST")}
                className={`flex-1 p-3 rounded-md border transition-colors text-left ${form.invoice_type === "GST" ? "border-blue-500 bg-blue-50" : "border-slate-200 hover:border-slate-300"}`}
                data-testid="invoice-type-gst"
              >
                <div className="text-xs uppercase tracking-wider text-slate-500">Option A</div>
                <div className="font-medium text-slate-900">GST invoice</div>
                <div className="text-xs text-slate-500 mt-0.5">Creator shares their invoice — you upload it</div>
              </button>
              <button
                onClick={() => set("invoice_type", "Non-GST")}
                className={`flex-1 p-3 rounded-md border transition-colors text-left ${form.invoice_type === "Non-GST" ? "border-blue-500 bg-blue-50" : "border-slate-200 hover:border-slate-300"}`}
                data-testid="invoice-type-nongst"
              >
                <div className="text-xs uppercase tracking-wider text-slate-500">Option B</div>
                <div className="font-medium text-slate-900">Non-GST invoice</div>
                <div className="text-xs text-slate-500 mt-0.5">You generate the invoice in your format</div>
              </button>
            </div>

            {form.invoice_type === "Non-GST" && (
              <div className="p-4 border border-slate-200 rounded-md bg-slate-50/50 space-y-3">
                <div className="text-xs uppercase tracking-wider text-slate-500 font-semibold">Details for generated invoice</div>
                <div className="grid grid-cols-2 gap-3">
                  <Field label="Legal name" v={form.legal_name} on={(v) => set("legal_name", v)} testid="field-legal-name" />
                  <Field label="PAN number" v={form.pan_number} on={(v) => set("pan_number", v)} testid="field-pan" />
                  <Field label="Bank account name" v={form.bank_account_name} on={(v) => set("bank_account_name", v)} testid="field-bank-name-account" />
                  <Field label="Bank account number" v={form.bank_account_number} on={(v) => set("bank_account_number", v)} testid="field-bank-number" />
                  <Field label="IFSC code" v={form.bank_ifsc} on={(v) => set("bank_ifsc", v)} testid="field-ifsc" />
                  <Field label="Bank name" v={form.bank_name} on={(v) => set("bank_name", v)} testid="field-bank-name" />
                  <Field label="Address" v={form.address} on={(v) => set("address", v)} testid="field-address" wide />
                </div>
                {editing?.id && (
                  <div className="flex justify-between items-center pt-3 border-t border-slate-200">
                    <div className="text-xs text-slate-500">Save any edits, then download the invoice PDF.</div>
                    <a
                      href={`${API}/creators/${editing.id}/invoice-pdf`}
                      target="_blank"
                      rel="noreferrer"
                      className="inline-flex items-center gap-2 text-sm font-medium px-3 py-1.5 rounded-md bg-slate-900 text-white hover:bg-slate-800"
                      data-testid="generate-non-gst-pdf"
                    >
                      <FilePdf size={14} /> Generate Non-GST PDF
                    </a>
                  </div>
                )}
              </div>
            )}

            {form.invoice_type === "GST" && (
              <div className="p-4 border border-slate-200 rounded-md bg-slate-50/50 space-y-3">
                <div className="text-xs uppercase tracking-wider text-slate-500 font-semibold">GST invoice details</div>
                <div className="grid grid-cols-2 gap-3">
                  <Field label="Legal name" v={form.legal_name} on={(v) => set("legal_name", v)} testid="field-gst-legal-name" />
                  <Field label="GST number" v={form.gst_number} on={(v) => set("gst_number", v)} testid="field-gst-number" />
                  <div>
                    <Label className="text-xs uppercase tracking-wider text-slate-500">Invoice date</Label>
                    <Input type="date" value={form.invoice_date || ""} onChange={(e) => set("invoice_date", e.target.value)} className="mt-1" data-testid="field-gst-invoice-date" />
                  </div>
                  <Field label="Invoice number" v={form.invoice_number} on={(v) => set("invoice_number", v)} testid="field-gst-invoice-number" />
                </div>
              </div>
            )}

            <div className="p-4 border border-dashed border-slate-300 rounded-md">
              <div className="text-xs uppercase tracking-wider text-slate-500 mb-2">Uploaded invoice document</div>
              {form.invoice_file_id ? (
                <div className="flex items-center justify-between bg-white border border-slate-200 rounded-md px-3 py-2">
                  <button
                    type="button"
                    onClick={() => openInvoice(form.invoice_file_id, form.invoice_filename)}
                    className="flex items-center gap-2 text-sm text-blue-600 hover:text-blue-700"
                    data-testid="invoice-view-link"
                  >
                    <FileText size={16} weight="duotone" /> {form.invoice_filename || "Invoice"}
                  </button>
                  <button
                    onClick={() => { set("invoice_file_id", null); set("invoice_filename", null); }}
                    className="text-slate-400 hover:text-rose-600"
                    data-testid="invoice-remove-btn"
                  >
                    <Trash size={16} />
                  </button>
                </div>
              ) : (
                <label className="flex flex-col items-center justify-center py-6 cursor-pointer text-slate-500 hover:text-blue-600">
                  <UploadSimple size={22} />
                  <div className="text-sm mt-2">{uploading ? "Uploading…" : "Click to upload PDF / image"}</div>
                  <div className="text-xs text-slate-400 mt-0.5">Max 15MB · PDF, JPG, PNG, WEBP</div>
                  <input
                    type="file"
                    accept="application/pdf,image/jpeg,image/png,image/webp"
                    onChange={(e) => e.target.files?.[0] && uploadInvoice(e.target.files[0])}
                    className="hidden"
                    data-testid="invoice-upload-input"
                  />
                </label>
              )}
            </div>
          </TabsContent>

          {/* Payments */}
          <TabsContent value="payment" className="space-y-3">
            <div className="grid grid-cols-3 gap-3">
              <div className="p-3 rounded-md bg-slate-50 border border-slate-200">
                <div className="text-[10px] uppercase tracking-wider text-slate-500">Final cost</div>
                <div className="stat-num text-lg font-semibold text-slate-900">{fmtCur(form.final_cost)}</div>
              </div>
              <div className="p-3 rounded-md bg-emerald-50 border border-emerald-200">
                <div className="text-[10px] uppercase tracking-wider text-emerald-700">Paid</div>
                <div className="stat-num text-lg font-semibold text-emerald-800">{fmtCur(totalPaid)}</div>
              </div>
              <div className="p-3 rounded-md bg-rose-50 border border-rose-200">
                <div className="text-[10px] uppercase tracking-wider text-rose-700">Pending</div>
                <div className="stat-num text-lg font-semibold text-rose-800">{fmtCur(pending)}</div>
              </div>
            </div>

            <Button
              onClick={() => onOpenPayments?.(editing)}
              disabled={!editing?.id}
              className="w-full bg-blue-600 hover:bg-blue-700 text-white"
              data-testid="log-payment-btn"
            >
              <Plus size={14} className="mr-1.5" /> Log a new payment
            </Button>

            {isAdmin && (
              <Button
                onClick={() => onOpenRazorpay?.(editing)}
                disabled={!editing?.id || pending <= 0}
                variant="outline"
                className="w-full border-blue-200 bg-blue-50 hover:bg-blue-100 text-blue-700 font-medium"
                data-testid="razorpay-pay-now-btn"
              >
                <Lightning size={14} className="mr-1.5" weight="fill" /> Pay now via Razorpay
              </Button>
            )}

            <div className="border border-slate-200 rounded-md">
              <div className="px-3 py-2 bg-slate-50 border-b border-slate-200 flex items-center justify-between text-xs">
                <span className="uppercase tracking-wider text-slate-500 font-semibold">Payment log</span>
                <Wallet size={14} className="text-slate-400" />
              </div>
              {payments.length === 0 ? (
                <div className="p-6 text-center text-sm text-slate-500">No payments logged yet</div>
              ) : (
                <div className="divide-y divide-slate-100">
                  {payments.map((p) => (
                    <div key={p.id} className="px-3 py-2.5 flex items-center justify-between gap-2 text-sm" data-testid={`payment-log-row-${p.id}`}>
                      <div className="min-w-0 flex-1">
                        <div className="font-medium text-slate-900">{fmtCur(p.amount)} · <span className="text-xs text-slate-500">{p.payment_type}</span></div>
                        <div className="text-xs text-slate-500 mt-0.5 break-all">
                          {new Date(p.paid_at).toLocaleString("en-IN", { day: "2-digit", month: "short", year: "numeric", hour: "2-digit", minute: "2-digit", hour12: true })} · {p.mode || "—"}{p.reference ? ` · ${p.reference}` : ""}
                        </div>
                      </div>
                      <div className="text-[10px] text-slate-400 shrink-0">by {p.created_by_name || "—"}</div>
                      {isAdmin && (
                        <button
                          onClick={() => setPaymentToDelete(p)}
                          className="p-1.5 text-slate-400 hover:text-rose-600 hover:bg-rose-50 rounded shrink-0"
                          title="Delete payment"
                          data-testid={`delete-payment-${p.id}`}
                        >
                          <Trash size={14} />
                        </button>
                      )}
                    </div>
                  ))}
                </div>
              )}
            </div>
          </TabsContent>
        </Tabs>

        <DialogFooter className="mt-4">
          <Button variant="outline" onClick={() => onOpenChange(false)} data-testid="dialog-cancel-btn">Close</Button>
          <Button onClick={save} disabled={saving} className="bg-blue-600 hover:bg-blue-700 text-white" data-testid="dialog-save-btn">
            {saving ? "Saving…" : editing ? "Save changes" : "Add creator"}
          </Button>
        </DialogFooter>
      </DialogContent>

      <PaymentDeleteDialog
        payment={paymentToDelete}
        onOpenChange={setPaymentToDelete}
        contextLabel={editing?.username ? `@${editing.username}` : null}
        onDeleted={async () => {
          if (editing?.id) {
            const { data } = await api.get(`/creators/${editing.id}/payments`);
            setPayments(data);
          }
          onSaved?.(editing);
        }}
      />
    </Dialog>
  );
}

function Field({ label, v, on, type = "text", wide, testid }) {
  return (
    <div className={wide ? "col-span-2" : ""}>
      <Label className="text-xs uppercase tracking-wider text-slate-500">{label}</Label>
      <Input
        type={type}
        value={v ?? ""}
        onChange={(e) => on(e.target.value)}
        className="mt-1"
        data-testid={testid}
      />
    </div>
  );
}
